import { FC, Fragment, useEffect, useState } from 'react';
import {
  ChangeFlightResponse,
  DynamicFlight,
  GrossAndTax,
} from 'ts/dynamic/interfaces/DynamicResponse';
import DynamicTicket from './DynamicTicket';
import Button from 'components/ui/Button/Button';
import { useTranslation } from 'react-i18next';
import { faArrowCircleLeft } from '@fortawesome/free-solid-svg-icons';
import { getSortFlightOptions } from 'constants/dynamic';
import { getSortedFlights } from 'utils/dynamic/dynamic';
import { filterDynamicFlights, initFlights } from 'utils/dynamic/dynamic-fl';
import DynamicFlightFilters from './DynamicFilters';
import { changeFlight } from 'services/dynamic';
import useRecaptcha from 'hooks/useRecaptcha';
import { showError } from 'utils/utils';
import LoaderMain from 'components/shared/LoaderMain';
import SortBy from 'components/shared/SortBy';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface ChangeFlightProps {
  flights: DynamicFlight[];
  selectedFlight: DynamicFlight;
  onSelectNewFlight: (res: ChangeFlightResponse) => void;
  onPressBack: () => void;
  searchToken: string;
  currency: keyof GrossAndTax;
}

const ChangeFlight: FC<ChangeFlightProps> = ({ selectedFlight, ...props }) => {
  const { t } = useTranslation();
  const { handleRecapcha } = useRecaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [flights, setFlights] = useState<DynamicFlight[]>(
    initFlights(
      props.flights,
      selectedFlight.flighPrice.grossAndTax[props.currency] as number,
      props.currency
    )
  );

  useEffect(() => {
    setFlights(
      initFlights(
        props.flights,
        selectedFlight.flighPrice.grossAndTax[props.currency] as number,
        props.currency
      )
    );
  }, [props.flights.length]); // eslint-disable-line

  const onChangeFlight = async (id: string) => {
    setIsLoading(true);
    const token = await handleRecapcha();
    if (!token) return;

    const data = await changeFlight(token, props.searchToken, id);
    setIsLoading(false);

    if (data && !data.hasErrors && data.flightChanged) {
      props.onSelectNewFlight({ ...data, id });
    } else {
      console.log('Dynamic/ChangeFlight ' + data?.errorMessage || 'Error');
      showError(t('something_went_wrong'));
    }
  };

  return (
    <Fragment>
      <div className="details">
        <div className="mb-20 default-shadow flex-between dyn-change-header">
          <Button
            title={t('back')}
            icon={faArrowCircleLeft}
            onClick={props.onPressBack}
            className="btn btn-link"
          />
          <SortBy
            onSortChange={(sort) => setFlights([...getSortedFlights(flights, sort)])}
            options={getSortFlightOptions(
              selectedFlight.outbound.departureAirport.code,
              selectedFlight.outbound.arrivalAirport.code
            )}
          />
        </div>
        {flights
          .filter((fl) => fl.visible)
          .map((flight) => (
            <DynamicTicket
              mode={selectedFlight.id === flight.id ? 1 : 2}
              choosenTicketPrice={selectedFlight.priceGrossAndTax}
              dynamicFlight={flight}
              onClickChange={() =>
                selectedFlight.id === flight.id ? props.onPressBack() : onChangeFlight(flight.id)
              }
              btnText={selectedFlight.id === flight.id ? t('dyn.your_choice') : t('choose')}
              isLoading={isLoading}
            />
          ))}
      </div>
      <div className="default-shadow details-summary">
        <DynamicFlightFilters
          flights={flights}
          onFiltering={(filter) => setFlights([...filterDynamicFlights(flights, filter)])}
        />
      </div>
      
        {isLoading && (
          <LoaderMain systemProducts="Dynamic" systemProductsDetails="LoadingDetails" />
        )}
      
    </Fragment>
  );
};

export default withErrorBoundary(ChangeFlight, 'ChangeFlight');
